import { Resolution } from '@api-types/enums/resolution.enum';
import * as Highcharts from 'highcharts';
import {
  AxisPlotBandsOptions,
  ExportingOptions,
  GradientColorObject,
  RangeSelectorButtonTypeValue,
  XAxisPlotLinesOptions,
} from 'highcharts';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getForecastRangeInitialType(
  resolution: Resolution,
): RangeSelectorButtonTypeValue {
  switch (resolution) {
    case Resolution.Daily:
      return 'day';
    case Resolution.Monthly:
      return 'month';
    case Resolution.Weekly:
      return 'week';
    case Resolution.Yearly:
      return 'year';
    default:
      throw new Error('Resolution not supported');
  }
}

/**
 *
 * @param initialDateCount How many units of the defined type the button should span. If type is "month" and count is 3, the button spans three months.
 * @param resolution
 */
export const FORECAST_RANGE_SELECTOR = (
  initialDateCount: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  resolution: Resolution,
): Highcharts.RangeSelectorOptions => ({
  enabled: true,
  inputEnabled: true,
  selected: undefined, // temporary fix to prevent weird jumping behaviour when switching resolution
  buttons: [
    {
      type: 'day', // getForecastRangeInitialType(resolution),
      // TODO when setting this currently we destroy the selector when switching the resolution.
      // We need to take in to account the resolution to fix this
      count: initialDateCount,
      text: 'Initial',
      title: 'View initial date',
    },
    {
      type: 'month',
      count: 1,
      text: '1m',
      title: 'View 1 month',
    },
    {
      type: 'month',
      count: 3,
      text: '3m',
      title: 'View 3 months',
    },
    {
      type: 'month',
      count: 6,
      text: '6m',
      title: 'View 6 months',
    },
    {
      type: 'ytd',
      text: 'YTD',
      title: 'View year to date',
    },
    {
      type: 'year',
      count: 1,
      text: '1y',
      title: 'View 1 year',
    },
    {
      type: 'all',
      text: 'All',
      title: 'View all',
    },
  ],
});

export const FORECAST_PLOT_BAND = (
  from: number,
  to: number,
): AxisPlotBandsOptions => ({
  color: {
    linearGradient: { x1: 1, y1: 0, x2: 0, y2: 0 },
    stops: [
      [0, 'rgba(0, 148, 255, 0)'],
      [1, 'rgba(0, 148, 255, 0.1)'],
    ],
  } as GradientColorObject,
  from,
  to,
  zIndex: 0,
  label: {
    useHTML: true,
    /* html */
    text: `
      <div class="forecast-plot-band-label font-body flex justify-between items-center">
        <div class="flex items-center mr-4">
          <div>
            <i class="material-icons text-sm">arrow_back</i>
          </div>
          <p class="ml-1">Actuals</p>
        </div>
        <div class="flex items-center text-primary">
          <p class="mr-1">Forecast</p>
          <div>
          <i class="material-icons text-sm">arrow_forward</i>
          </div>
        </div>
      </div>`,
    align: 'left',
    style: {
      display: 'flex',
    },
    x: -69,
  },
});

export const FORECAST_PLOT_LINE = (
  position: number,
): XAxisPlotLinesOptions => ({
  // color: '#0087E9',
  value: position,
  width: 2,
  zIndex: 4,
  className: 'stroke-primary',
});

export const FORECAST_EXPORT_OPTIONS = (
  additionalTranslations: { low: string; high: string },

  dateFormat = '%a, %e %b , %Y',

  chartName = '',
  handleViewTable: () => void,
): ExportingOptions => ({
  enabled: true,
  allowTableSorting: false,
  fallbackToExportServer: false,
  tableCaption: chartName,

  buttons: {
    contextButton: {
      menuItems: [
        'viewFullscreen',
        'printChart',
        'separator',
        'downloadPNG',
        'downloadJPEG',
        // 'downloadPDF',
        'downloadSVG',
        'separator',
        'downloadCSV',
        'downloadXLS',
        'viewData',
      ],
    },
  },
  menuItemDefinitions: {
    viewData: {
      textKey: 'viewData',
      onclick: () => {
        handleViewTable();
      },
    },
  },
  csv: {
    dateFormat,
    itemDelimiter: ',',
    columnHeaderFormatter(
      item: Highcharts.Series | Highcharts.Axis,
      key: 'high' | 'low',
      keyLength: number,
    ) {
      if (!item || item instanceof Highcharts.Axis) {
        return (item as Highcharts.Axis).options.title?.text;
      }
      // Item is not axis, now we are working with series.
      // Key is the property on the series we show in this column.
      const { name } = item as Highcharts.Series;
      if (keyLength <= 1) {
        return name;
      }
      return {
        topLevelColumnTitle: name,
        columnTitle: additionalTranslations[key],
      };
    },
  },
});

export function handleChartExport(
  event: Highcharts.ExportDataEventObject,
): void {
  const options = Highcharts.getOptions();

  function fixSeparatorAndDecimalPoint(num: number): string {
    // round to 2 decimal places
    const roundedNum = Math.round(num * 100) / 100;
    // fill with 0 if number has decimal places, but less than 2
    let numString =
      roundedNum % 1 === 0 ? roundedNum.toString() : roundedNum.toFixed(2);

    if (!options.lang) {
      throw new Error('options.lang is undefined');
    }
    if (!options.lang.decimalPoint) {
      throw new Error('options.lang.decimalPoint is undefined');
    }
    // replace decimal point
    numString = numString.toString().replace('.', options.lang.decimalPoint);

    // add thousands separator
    if (numString.length < 50) {
      numString = numString.replace(
        /\d{1,3}(?=(\d{3})+(?!\d))/g,
        `$&${options.lang.thousandsSep}`,
      );
    }
    return numString;
  }

  event.dataRows.forEach((row: Array<string | number>) => {
    row.forEach((entry, index) => {
      // format all numbers in the table
      if (typeof entry === 'number') {
        row[index] = fixSeparatorAndDecimalPoint(entry);
      } else if (Array.isArray(entry)) {
        entry.forEach((subElement: number, subIndex: number) => {
          entry[subIndex] = fixSeparatorAndDecimalPoint(subElement);
        });
      }
    });
  });
}
